import { Fragment } from "preact";
import type { VNode } from "preact";

/**
 * A deliberately small markdown renderer for assistant replies.
 *
 * The agent writes prose the way a model does — `**bold**` for program names,
 * `-` bullets for what a tier includes, bare URLs for the scheduler. Rendered as
 * plain text those read as literal asterisks and naked links, which is the first
 * thing anyone notices about a chat widget.
 *
 * Two constraints shaped this:
 *
 *  - **VNodes, not HTML.** Nothing here ever touches `dangerouslySetInnerHTML`,
 *    so model output cannot inject markup into the page. `safeHref` additionally
 *    drops any scheme that isn't http/https/tel/mailto.
 *  - **Partial input is normal.** It re-parses on every streamed delta, so it is
 *    called with half-finished syntax constantly. Unmatched markers just render
 *    as the literal characters until their closing pair arrives.
 */

/** Ordered by precedence — the first alternative to match a span wins. */
const INLINE =
  /(\*\*[^\n]+?\*\*|__[^\n]+?__|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\)|\*[^*\n]+?\*|\bhttps?:\/\/[^\s<>()]+[^\s<>().,;:!?])/;

const SAFE_SCHEME = /^(https?:|tel:|mailto:)/i;

function safeHref(url: string): string {
  return SAFE_SCHEME.test(url) ? url : "#";
}

/**
 * Links to the page we are embedded on navigate in place; anything third-party
 * opens in a new tab.
 *
 * In-place is the right default for the practice's own pages — memberships, FAQ,
 * a service page — because the widget rehydrates from the stored session on the
 * next page and the conversation simply continues. That is the whole point of the
 * memory, and it is visible to the visitor. Third-party pages (the scheduler, the
 * portal signup) get a new tab instead: those are forms, and navigating away
 * mid-form to come back later loses their work.
 */
function linkTarget(url: string): { target?: string; rel?: string } {
  try {
    if (new URL(url, location.href).hostname === location.hostname) return {};
  } catch {
    /* fall through to the safe default */
  }
  return { target: "_blank", rel: "noopener noreferrer" };
}

/** Scheduler links are long; show the host and drop the query string. */
function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    return `${u.host}${path}`;
  } catch {
    return url;
  }
}

function inline(text: string): (string | VNode)[] {
  const out: (string | VNode)[] = [];
  let key = 0;

  for (const part of text.split(INLINE)) {
    if (!part) continue;

    if (part.length > 4 && (part.startsWith("**") || part.startsWith("__")) ) {
      out.push(<strong key={key++}>{part.slice(2, -2)}</strong>);
    } else if (part.length > 2 && part.startsWith("`")) {
      out.push(
        <code class="inline-code" key={key++}>
          {part.slice(1, -1)}
        </code>,
      );
    } else if (part.startsWith("[")) {
      const m = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
      if (m) {
        out.push(
          <a key={key++} href={safeHref(m[2])} {...linkTarget(m[2])}>
            {m[1]}
          </a>,
        );
      } else {
        out.push(part);
      }
    } else if (part.length > 2 && part.startsWith("*")) {
      out.push(<em key={key++}>{part.slice(1, -1)}</em>);
    } else if (/^https?:\/\//.test(part)) {
      out.push(
        <a key={key++} href={safeHref(part)} {...linkTarget(part)}>
          {prettyUrl(part)}
        </a>,
      );
    } else {
      out.push(part);
    }
  }

  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,4}\s+(.*)$/;

export function Markdown({ text }: { text: string }): VNode {
  const blocks: VNode[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];
  let ordered = false;
  let key = 0;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(<p key={key++}>{inline(paragraph.join("\n"))}</p>);
    paragraph = [];
  };

  const flushList = () => {
    if (!items.length) return;
    const rendered = items.map((item, i) => <li key={i}>{inline(item)}</li>);
    blocks.push(ordered ? <ol key={key++}>{rendered}</ol> : <ul key={key++}>{rendered}</ul>);
    items = [];
  };

  for (const line of text.split("\n")) {
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push(<h5 key={key++}>{inline(heading[1])}</h5>);
    } else if (bullet || numbered) {
      flushParagraph();
      const isOrdered = Boolean(numbered);
      // A bulleted list following a numbered one (or vice versa) is a new list.
      if (items.length && isOrdered !== ordered) flushList();
      ordered = isOrdered;
      items.push((bullet ?? numbered)![1]);
    } else if (!line.trim()) {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line);
    }
  }

  flushParagraph();
  flushList();

  return <Fragment>{blocks}</Fragment>;
}
